import { AxiosResponse } from "axios";
import { useCallback } from "react";
import {
  deleteBuildingDeskGroupAsync,
  deleteBuildingFacilityAsync,
  getBuildingConveniencesAsync,
  getBuildingDeskGroupsAsync,
  getBuildingDesksAsync,
  getBuildingMeetingRoomsAsync,
  patchBuildingConvenienceAsync,
  patchBuildingDeskAsync,
  patchBuildingDeskGroupAsync,
  patchBuildingMeetingRoomAsync,
  postBuildingConvenienceAsync,
  postBuildingDeskAsync,
  postBuildingDeskGroupAsync,
  postBuildingMeetingRoomAsync,
} from "src/api/building/building-api";
import {
  BuildingCommonFacilityDeskGroupModel,
  BuildingDeskGroupModel,
  BuildingDeskGroupRequest,
  BuildingDeskModel,
  BuildingDeskRequest,
  BuildingFacilityModel,
  BuildingFacilityRequest,
  CommonFacilityModel,
  CommonFacilityType,
  SpaceStyle,
} from "src/api/building/building-types";
import { BuildingFacilityTagModel } from "src/api/building/commonfacility-types";
import { useApiOperation } from "src/api/hooks";
import { ApiResponse, MediaFile } from "src/api/public-types";

/*
  공용공간(회의실/편의시설/좌석그룹/좌석) RPBF 기반 CRUD 훅.
  기존 useFacility(전체 buildingCommonFacility 를 매번 replace 하던 헤비 API)를 대체한다.
  - 회의실/편의시설: 목록 RPBF01 / 등록 RPBF02 / 수정 RPBF03 / 삭제 RPBF07
  - 좌석 그룹: 목록 RPBF08 / 등록 RPBF09 / 수정 RPBF10 / 삭제 RPBF11
  - 좌석: 목록 RPBF04 / 등록 RPBF05 / 수정 RPBF06 / 삭제 RPBF07
  컬럼/폼 모달은 기존 CommonFacilityModel / DeskSpace 형태를 그대로 사용하므로, 이 훅 경계에서 RPBF 모델과 매핑한다.
*/

// MediaFile[] -> RPBF 등록/수정 media request
const toMediaRequest = (mediaList?: MediaFile[]) =>
  (mediaList ?? []).map((media) => ({
    media,
    primary: !!media.isPrimary,
    description: media.description,
  }));

// RPBF FacilityMedia[] -> MediaFile[] (컬럼/모달 표시용)
const toMediaList = (media?: BuildingFacilityModel["media"]): MediaFile[] =>
  (media ?? []).map((item) => ({
    ...(item.media ?? {}),
    isPrimary: item.primary,
    description: item.description,
  }));

// RPBF BuildingFacilityModel -> CommonFacilityModel (컬럼/모달 표시용)
export const mapBuildingFacilityToCommon = (facility: BuildingFacilityModel, facilityType: CommonFacilityType): CommonFacilityModel => {
  const floor = Number(facility.floor ?? 0);
  return {
    id: facility.facilityId !== undefined ? Number(facility.facilityId) : undefined,
    buildingId: facility.buildingId !== undefined ? Number(facility.buildingId) : undefined,
    commonFacilityType: facilityType,
    isGround: floor > 0,
    floorNum: Math.abs(floor),
    facilityName: facility.name,
    spaceStyle: (facility.spaceStyle as SpaceStyle) || "SPACE_STYLE_UNRECOGNIZED",
    locationCode: facility.locationCode,
    facilityMaxPeopleNums: facility.maxCapacity,
    // amenities(콤마 구분 문자열) <-> 기존 facilityList(배열)/equipments(문자열)
    facilityList: facility.amenities ? facility.amenities.split(",") : [],
    description: facility.description,
    mediaList: toMediaList(facility.media),
    facilityTagList: (facility.tags ?? []).map(
      (tag): BuildingFacilityTagModel => ({
        id: tag.id ?? "",
        partnerId: "",
        name: tag.name ?? "",
        priority: tag.priority ?? 0,
        facilityIds: [],
      }),
    ),
  };
};

// 폼이 넘겨준 CommonFacilityModel(passData) -> RPBF02/03 request body
export const mapCommonToFacilityRequest = (data: CommonFacilityModel): BuildingFacilityRequest => ({
  // 폼의 floorNum 은 부호 포함 값(지하 -3 등). RPBF floor 는 부호 그대로 사용.
  floor: data.floorNum !== undefined ? Number(data.floorNum) : undefined,
  name: data.facilityName,
  spaceStyle: data.spaceStyle,
  locationCode: data.locationCode,
  maxCapacity: data.facilityMaxPeopleNums !== undefined ? Number(data.facilityMaxPeopleNums) : undefined,
  tagIds: (data.facilityTagList ?? []).map((tag) => Number(tag.id)),
  amenities: data.equipments,
  description: data.description,
  media: toMediaRequest(data.mediaList),
});

// RPBF 좌석그룹(RPBF08) + 좌석(RPBF04) -> BuildingCommonFacilityDeskGroupModel[] (기존 deskSpaces 빌더가 소비하는 형태)
export const mapDeskGroupsToModel = (groups: BuildingDeskGroupModel[], desks: BuildingDeskModel[]): BuildingCommonFacilityDeskGroupModel[] =>
  groups.map((group) => {
    const floor = Number(group.floor ?? 0);
    const groupDesks = desks.filter((desk) => String(desk.deskGroupId) === String(group.id));
    return {
      id: group.id !== undefined ? Number(group.id) : undefined,
      groupName: group.name,
      isGround: floor > 0,
      floorNum: Math.abs(floor),
      description: group.description,
      deskChartDescription: group.deskChart?.description,
      buildingId: group.buildingId !== undefined ? Number(group.buildingId) : undefined,
      mediaList: toMediaList(group.media),
      deskList: groupDesks.map(
        (desk): CommonFacilityModel => ({
          id: desk.facilityId !== undefined ? Number(desk.facilityId) : undefined,
          facilityName: desk.name,
          locationCode: desk.locationCode,
          commonFacilityType: "DESK",
          buildingCommonFacilityDeskGroupId: desk.deskGroupId !== undefined ? Number(desk.deskGroupId) : undefined,
          centerX: desk.center?.x !== undefined ? String(desk.center.x) : undefined,
          centerY: desk.center?.y !== undefined ? String(desk.center.y) : undefined,
        }),
      ),
    };
  });

// 좌석그룹 폼(BuildingCommonFacilityDeskGroupModel) -> RPBF09/10 request body
export const mapDeskGroupToRequest = (data: BuildingCommonFacilityDeskGroupModel): BuildingDeskGroupRequest => ({
  // 폼의 floorNum 은 부호 포함 값
  floor: data.floorNum !== undefined ? Number(data.floorNum) : undefined,
  name: data.groupName,
  description: data.description,
  media: toMediaRequest(data.mediaList),
});

// 좌석 폼(CommonFacilityModel) -> RPBF05/06 request body
export const mapDeskToRequest = (data: CommonFacilityModel): BuildingDeskRequest => ({
  deskGroupId: data.buildingCommonFacilityDeskGroupId !== undefined ? String(data.buildingCommonFacilityDeskGroupId) : undefined,
  name: data.facilityName,
  locationCode: data.locationCode,
});

const useFacilityV2 = () => {
  // 회의실/편의시설
  const { executeAsync: getMeetingRooms } = useApiOperation(getBuildingMeetingRoomsAsync);
  const { executeAsync: getConveniences } = useApiOperation(getBuildingConveniencesAsync);
  const { executeAsync: postMeetingRoom } = useApiOperation(postBuildingMeetingRoomAsync);
  const { executeAsync: postConvenience } = useApiOperation(postBuildingConvenienceAsync);
  const { executeAsync: patchMeetingRoom } = useApiOperation(patchBuildingMeetingRoomAsync);
  const { executeAsync: patchConvenience } = useApiOperation(patchBuildingConvenienceAsync);
  const { executeAsync: deleteFacility } = useApiOperation(deleteBuildingFacilityAsync);

  // 좌석 그룹 / 좌석
  const { executeAsync: getDeskGroups } = useApiOperation(getBuildingDeskGroupsAsync);
  const { executeAsync: getDesks } = useApiOperation(getBuildingDesksAsync);
  const { executeAsync: postDeskGroup } = useApiOperation(postBuildingDeskGroupAsync);
  const { executeAsync: patchDeskGroup } = useApiOperation(patchBuildingDeskGroupAsync);
  const { executeAsync: deleteDeskGroup } = useApiOperation(deleteBuildingDeskGroupAsync);
  const { executeAsync: postDesk } = useApiOperation(postBuildingDeskAsync);
  const { executeAsync: patchDesk } = useApiOperation(patchBuildingDeskAsync);

  const isMeetingRoom = (facilityType: CommonFacilityType) => facilityType === "MEETING_ROOM";

  // === 회의실/편의시설 ===

  // 목록 조회 후 CommonFacilityModel[] 로 매핑
  const fetchFacilities = useCallback(
    async (facilityType: CommonFacilityType, buildingId: number): Promise<CommonFacilityModel[]> => {
      const exec = isMeetingRoom(facilityType) ? getMeetingRooms : getConveniences;
      const { data } = await exec({ buildingId });
      const list = data?.data?.content ?? [];
      return list.map((facility) => mapBuildingFacilityToCommon(facility, facilityType));
    },
    [getMeetingRooms, getConveniences],
  );

  // 등록/수정 (id 유무로 분기)
  const saveFacility = useCallback(
    async (
      facilityType: CommonFacilityType,
      buildingId: number,
      data: CommonFacilityModel,
    ): Promise<AxiosResponse<ApiResponse<{ facility: BuildingFacilityModel }>>> => {
      const body = mapCommonToFacilityRequest(data);
      if (data.id) {
        const exec = isMeetingRoom(facilityType) ? patchMeetingRoom : patchConvenience;
        return await exec({ buildingId, facilityId: Number(data.id), facility: body });
      }
      const exec = isMeetingRoom(facilityType) ? postMeetingRoom : postConvenience;
      return await exec({ buildingId, facility: body });
    },
    [patchMeetingRoom, patchConvenience, postMeetingRoom, postConvenience],
  );

  // 삭제 (RPBF07 공통 facilities 엔드포인트 - 회의실/편의시설/좌석 공통)
  const removeFacility = useCallback(
    async (buildingId: number, facilityId: number) => {
      return await deleteFacility({ buildingId, facilityId });
    },
    [deleteFacility],
  );

  // === 좌석 그룹 / 좌석 ===

  // 좌석그룹(RPBF08) + 좌석(RPBF04) 조회 후 BuildingCommonFacilityDeskGroupModel[] 로 재구성
  const fetchDeskGroups = useCallback(
    async (buildingId: number): Promise<BuildingCommonFacilityDeskGroupModel[]> => {
      const [groupsRes, desksRes] = await Promise.all([getDeskGroups({ buildingId }), getDesks({ buildingId })]);
      const groups = groupsRes.data?.data?.content ?? [];
      const desks = desksRes.data?.data?.content ?? [];
      return mapDeskGroupsToModel(groups, desks);
    },
    [getDeskGroups, getDesks],
  );

  // 좌석그룹 등록/수정 (RPBF09/10)
  const saveDeskGroup = useCallback(
    async (
      buildingId: number,
      data: BuildingCommonFacilityDeskGroupModel,
    ): Promise<AxiosResponse<ApiResponse<{ deskGroup: BuildingDeskGroupModel }>>> => {
      const body = mapDeskGroupToRequest(data);
      if (data.id) {
        return await patchDeskGroup({ buildingId, deskGroupId: Number(data.id), deskGroup: body });
      }
      return await postDeskGroup({ buildingId, deskGroup: body });
    },
    [patchDeskGroup, postDeskGroup],
  );

  // 좌석그룹 삭제 (RPBF11)
  const removeDeskGroup = useCallback(
    async (buildingId: number, deskGroupId: number) => {
      return await deleteDeskGroup({ buildingId, deskGroupId });
    },
    [deleteDeskGroup],
  );

  // 좌석 등록/수정 (RPBF05/06)
  const saveDesk = useCallback(
    async (buildingId: number, data: CommonFacilityModel): Promise<AxiosResponse<ApiResponse<{ desk: BuildingDeskModel }>>> => {
      const body = mapDeskToRequest(data);
      if (data.id) {
        return await patchDesk({ buildingId, facilityId: Number(data.id), desk: body });
      }
      return await postDesk({ buildingId, desk: body });
    },
    [patchDesk, postDesk],
  );

  return {
    // 회의실/편의시설
    fetchFacilities,
    saveFacility,
    removeFacility,
    // 좌석 그룹 / 좌석
    fetchDeskGroups,
    saveDeskGroup,
    removeDeskGroup,
    saveDesk,
  };
};

export default useFacilityV2;
